import { type UserId } from 'features/users/types';
import { canExposeInternals } from 'utils/development';
import { exportKey } from 'features/e2ee/utils/exportKey';
import { hkdf } from 'features/e2ee/utils/hkdf';
import { generateSharedKey } from 'features/e2ee/utils/generateSharedKey';
import { logger } from 'utils/logger';
import { store } from 'store/store';
import { applicationKeyGenerated } from 'features/e2ee/actions';
import { AesCm256EncryptionKey } from 'features/e2ee/AesCm256EncryptionKey';
import { E2eeReceiver } from 'features/e2ee/E2eeReceiver';
import { PayloadAction } from '@reduxjs/toolkit';
import { E2EEWhiteboardManager } from 'features/e2ee/E2EEWhiteboardManager';
import { DataDecryptor } from 'features/e2ee/core/DataDecryptor';
import type { DecryptDataOptions, EncryptedData } from 'features/e2ee/types';
import { selectUserById } from 'features/users/usersSlice';

export const e2eeNameSensitiveEvents = new Set(['handRaised']);

export interface EnableE2eeOptions {
  ellipticCurveKeys?: boolean;
}

export class E2EE {
  private enabled: boolean = false;

  private ellipticCurveKeys?: CryptoKeyPair;

  private receivers = new Map<string, E2eeReceiver>();

  private pendingReceiverDecryption = new Map<
    string,
    Array<{ data: EncryptedData; options?: DecryptDataOptions }>
  >();

  private pendingActions = new Map<string, Map<string, PayloadAction<any>>>();

  whiteboard: E2EEWhiteboardManager = new E2EEWhiteboardManager();

  streamKey = new AesCm256EncryptionKey();

  applicationKey = new AesCm256EncryptionKey();

  private dataDecryptor = new DataDecryptor(this.whiteboard);

  private makeReceiverKey(userId: UserId, roomId: string) {
    if (!userId) {
      throw new Error('Invalid userId provided for building receiver key');
    }

    if (!roomId) {
      throw new Error('Invalid roomId provided for building receiver key');
    }

    return JSON.stringify({ userId, roomId });
  }

  private parseReceiverKey(key: string): { userId: UserId; roomId: string } {
    try {
      return JSON.parse(key);
    } catch {
      throw new Error('Invalid receiver key format');
    }
  }

  private generateEllipticCurveKeys = async () => {
    this.ellipticCurveKeys = await window.crypto.subtle.generateKey(
      {
        name: 'ECDH',
        namedCurve: 'P-256',
      },
      true,
      ['deriveKey', 'deriveBits']
    );
  };

  private generateSharedSecret = async (targetPublicKey: CryptoKey) => {
    if (!this.ellipticCurveKeys) {
      throw new Error('Cannot generate a shared secret. Elliptic curve keys are not defined');
    }

    const sharedKey = await generateSharedKey(targetPublicKey, this.ellipticCurveKeys.privateKey);
    const derivedKey = await hkdf(sharedKey);

    return window.crypto.subtle.importKey('raw', derivedKey, { name: 'AES-GCM' }, false, [
      'encrypt',
      'decrypt',
    ]);
  };

  private queueReceiverDecryption(
    receiverKey: string,
    data: EncryptedData,
    options?: DecryptDataOptions
  ) {
    const pendingData = this.pendingReceiverDecryption.get(receiverKey) ?? [];
    pendingData.push({ data, options });

    this.pendingReceiverDecryption.set(receiverKey, pendingData);
  }

  generateApplicationKey = async () => {
    await this.applicationKey.generate({ hkdf: true });

    store.dispatch(applicationKeyGenerated());
  };

  generateStreamKey = async () => this.streamKey.generate();

  importPublicKey = async (publicKeyString: string) => {
    const parsedKey = JSON.parse(publicKeyString);
    return crypto.subtle.importKey(
      'jwk',
      parsedKey,
      {
        name: 'ECDH',
        namedCurve: parsedKey.crv,
      },
      true,
      parsedKey.key_ops
    );
  };

  private async processPendingDecryption(userId: UserId, roomId: string): Promise<void> {
    const receiverKey = this.makeReceiverKey(userId, roomId);

    const encryptedDataArray = this.pendingReceiverDecryption.get(receiverKey);
    if (encryptedDataArray) {
      logger
        .remote({ system: true, capture: 'e2ee' })
        .debug(
          `Processing pending decryption data of receiverId=${userId}, roomId=${roomId}, data=${JSON.stringify(encryptedDataArray)}`
        );

      await Promise.all(
        encryptedDataArray.map(({ data, options }) =>
          this.decryptRemoteData(userId, roomId, data, options)
        )
      );
    }
  }

  getStreamKey = () => this.streamKey;

  getApplicationKey = () => this.applicationKey;

  generateKeys = async (options: EnableE2eeOptions = { ellipticCurveKeys: true }) => {
    logger.remote({ system: true, capture: 'e2ee' }).debug('Generating encryption keys');

    const promises = [this.generateStreamKey(), this.generateApplicationKey()];

    if (options.ellipticCurveKeys) {
      promises.push(this.generateEllipticCurveKeys());
    }

    return Promise.all(promises);
  };

  get e2eeEnabled() {
    return this.enabled;
  }

  enableE2ee = async (options: EnableE2eeOptions = { ellipticCurveKeys: true }) => {
    this.enabled = true;

    await this.generateKeys(options);
  };

  exportPublicKey = async () => {
    if (!this.ellipticCurveKeys) {
      throw new Error('Cannot export a public key. Elliptic curve keys are not defined');
    }

    const key = await exportKey(this.ellipticCurveKeys.publicKey);

    return JSON.stringify(key);
  };

  exchangeEncryptionKey = async (userId: UserId, roomId: string, publicKeyString: string) => {
    const receiver = this.getReceiver(userId, roomId);
    // return an existing shared secret if it was generated before
    if (receiver?.sharedSecret) {
      // e2eeId is late-bound: unavailable during waiting room / breakout room
      // key exchange (publicKeyShared), populated after roomJoined/userJoined
      if (receiver.e2eeId == null) {
        const user = selectUserById(store.getState(), userId);
        if (user?.e2eeId != null) {
          receiver.e2eeId = user.e2eeId;
        }
      }

      return receiver.sharedSecret;
    }

    logger
      .remote({ system: true, capture: 'e2ee' })
      .info(`Exchanging an encryption key with the userId=${userId}, roomId=${roomId}`);

    if (!publicKeyString) {
      throw new Error('Cannot exchange an encryption key. Remote public key is not defined.');
    }

    const publicKey = await this.importPublicKey(publicKeyString);

    const sharedSecret = await this.generateSharedSecret(publicKey);
    if (!sharedSecret) {
      throw new Error('Cannot exchange an encryption key. Shared secret is not defined');
    }

    const user = selectUserById(store.getState(), userId);

    this.addReceiver(
      userId,
      roomId,
      new E2eeReceiver(userId, roomId, { publicKey, sharedSecret, e2eeId: user?.e2eeId })
    );

    const receiverKey = this.makeReceiverKey(userId, roomId);
    if (this.pendingReceiverDecryption.has(receiverKey)) {
      await this.processPendingDecryption(userId, roomId);
    }

    return sharedSecret;
  };

  decryptRemoteData = async (
    userId: UserId,
    roomId: string,
    data: EncryptedData,
    options: DecryptDataOptions = {}
  ) => {
    const receiverKey = this.makeReceiverKey(userId, roomId);
    const receiver = this.receivers.get(receiverKey);

    if (!receiver) {
      logger
        .remote({ system: true, capture: 'e2ee' })
        .debug(
          `Received remote encryption data while local ECDH computation has not finished yet. Adding usedId=${userId}, roomId=${roomId} to the decryption queue.`
        );

      this.queueReceiverDecryption(receiverKey, data, options);

      return;
    }

    logger
      .remote({ system: true, capture: 'e2ee' })
      .debug(`Storing remote encryption data of the userId=${userId}, roomId=${roomId}`);

    if (this.pendingReceiverDecryption.has(receiverKey)) {
      this.pendingReceiverDecryption.delete(receiverKey);
    }

    if (!receiver.sharedSecret) {
      throw new Error('Cannot decrypt a remote encryption data. Shared secret is not defined');
    }

    await this.dataDecryptor.decryptRemoteData(data, receiver, { rename: options.rename });
  };

  cleanup = () => {
    logger.remote({ system: true, capture: 'e2ee' }).info('Cleaning E2EE keys');

    this.receivers.clear();
    this.pendingReceiverDecryption.clear();
    this.pendingActions.clear();
  };

  removeReceiver = (userId: UserId, roomId: string) => {
    const key = this.makeReceiverKey(userId, roomId);

    this.receivers.delete(key);
    this.pendingReceiverDecryption.delete(key);
    this.pendingActions.delete(key);
  };

  getReceiver = (userId: UserId, roomId: string) => {
    const key = this.makeReceiverKey(userId, roomId);
    return this.receivers.get(key);
  };

  addReceiver = (userId: UserId, roomId: string, receiver: E2eeReceiver) => {
    const key = this.makeReceiverKey(userId, roomId);
    this.receivers.set(key, receiver);
  };

  updateReceiverE2eeId = (userId: UserId, roomId: string, e2eeId: number) => {
    const receiver = this.getReceiver(userId, roomId);
    if (receiver && receiver.e2eeId == null) {
      receiver.e2eeId = e2eeId;
    } else if (receiver && receiver.e2eeId != null && receiver.e2eeId !== e2eeId) {
      logger
        .remote({ system: true, capture: 'e2ee' })
        .warn(
          `[E2EE] Stale e2eeId detected: receiver has ${receiver.e2eeId}, expected ${e2eeId} for userId=${userId}`
        );
    }
  };

  enqueuePendingAction = (action: PayloadAction<any>, roomId: string) => {
    const initiatorId: UserId = action.payload.initiator?.id || action.payload.id;

    const receiver = this.getReceiver(initiatorId, roomId);
    if (!receiver?.name) {
      logger
        .remote({ system: true, capture: 'e2ee' })
        .debug(`Receiver's name is not known yet. Putting the action=${action.type} to the queue`);

      const receiverKey = this.makeReceiverKey(initiatorId, roomId);
      const pendingActions = this.pendingActions.get(receiverKey);

      if (!pendingActions) {
        this.pendingActions.set(receiverKey, new Map([[action.type, action]]));
      } else {
        pendingActions.set(action.type, action);
      }

      return true;
    }

    return false;
  };

  releasePendingActions = (userId: UserId, roomId: string) => {
    const receiverKey = this.makeReceiverKey(userId, roomId);
    const pendingActions = this.pendingActions.get(receiverKey);
    if (pendingActions) {
      const queue = new Map(pendingActions);
      queue.forEach((action, actionType) => {
        logger
          .remote({ system: true, capture: 'e2ee' })
          .debug(
            `Releasing pending action=${action.type} of a receiverId=${userId}, roomId=${roomId}`
          );

        store.dispatch(action);

        pendingActions.delete(actionType);
      });
    }
  };
}

export const E2EEManager = new E2EE();

// @TODO FIXME remove at some point
if (canExposeInternals()) {
  window.E2EE = E2EEManager;
}
